import { createFileRoute, notFound } from "@tanstack/react-router";
import Component from "@/routes/(inventory)/branch/_layout/inventory/item/-components/component";
import { SearchParamsWithYear } from "../available-inventory/-data/model";
import { Loading } from "@/layouts/loading";
import { GetInventoryResponse } from "@/gen/inventory/get_inventory_pb";
import { pharmacyClient } from "@/connect";
import { ItemType } from "@/gen/inventory/common_pb";
import { MedicalMaster, PriceHistory } from "@/gen/medical_master/common_pb";
import { NonMedicalMaster } from "@/gen/non_medical_master/common_pb";
import { StockMovementReportResponse } from "@/gen/inventory/stock_movement_report_pb";

type Props = {
    inventoryData: GetInventoryResponse,
    medicalMaster?: MedicalMaster,
    nonMedicalMaster?: NonMedicalMaster,
    itemType: ItemType,
    medicalPricingHistory?: PriceHistory[],
    stockMovementReport: StockMovementReportResponse
}

export const Route = createFileRoute("/(inventory)/branch/_layout/inventory/item/$type/$itemId")({
    validateSearch: SearchParamsWithYear,
    loaderDeps: ({ search: { year, out_of_stock, expired, expiring_in_30_days, available } }) => ({ year, expired, expiring_in_30_days, out_of_stock, available }),
    loader: async ({ params, deps }): Promise<Props> => {
        if (params.type !== "MEDICAL" && params.type !== "NON_MEDICAL") {
            throw notFound();
        }
        if (!params.itemId) {
            throw notFound();
        }

        if (!deps.available && !deps.expired && !deps.expiring_in_30_days && !deps.out_of_stock) {
            throw notFound();
        }

        if (params.type === "MEDICAL") {
            const inventoryData = await pharmacyClient.getInventory({
                page: 1,
                pageSize: 1000,
                filter: {
                    masterId: params.itemId,
                    isAvailable: deps.available,
                    isExpired: deps.expired,
                    expiringSoonDays: deps.expiring_in_30_days ? 30 : 0,
                    isOutOfStock: deps.out_of_stock,
                    itemType: ItemType.MEDICAL,
                }
            })
            const itemDetails = await pharmacyClient.getMedicalMaster({
                id: params.itemId
            })
            const medicalPricingHistory = await pharmacyClient.getPriceHistory({
                id: params.itemId,
                page: 1,
                pageSize: 1000,
            })
            const stockMovementReport = await pharmacyClient.stockMovementReport({
                masterId: params.itemId,
                itemType: ItemType.MEDICAL,
                year: deps.year
            })
            return {
                inventoryData: inventoryData,
                medicalMaster: itemDetails.medicalMaster,
                itemType: ItemType.MEDICAL,
                medicalPricingHistory: medicalPricingHistory.priceHistories,
                stockMovementReport: stockMovementReport
            }
        } else {
            const inventoryData = await pharmacyClient.getInventory({
                page: 1,
                pageSize: 1000,
                filter: {
                    masterId: params.itemId,
                    isAvailable: deps.available,
                    isExpired: deps.expired,
                    expiringSoonDays: deps.expiring_in_30_days ? 30 : 0,
                    isOutOfStock: deps.out_of_stock,
                    itemType: ItemType.NON_MEDICAL,
                }
            })
            const nonMedicalDetails = await pharmacyClient.getNonMedicalMaster({
                id: params.itemId
            })
            const stockMovementReport = await pharmacyClient.stockMovementReport({
                masterId: params.itemId,
                itemType: ItemType.NON_MEDICAL,
                year: deps.year
            })
            return {
                inventoryData: inventoryData,
                nonMedicalMaster: nonMedicalDetails.nonMedicalMaster,
                itemType: ItemType.NON_MEDICAL,
                stockMovementReport: stockMovementReport
            }
        }
    },
    component: Component,
    pendingComponent: () => <Loading />,
    pendingMinMs: 1000,
    pendingMs: 1
});